import { useMemo, useState } from "react";

import { HISTORICO_SIMULACAO_TABLE_DENSITY_KEY } from "@/core/constants/keys";
import {
  TABLE_SETTINGS_APPLIED_MESSAGE,
  useLastKnownTotal,
  useTableSettings,
  useTableState,
  useToast,
} from "@/ds";
import type { FilterDefinition, FilterValue } from "@/ds";
import { HISTORICO_SIMULACAO_TITULO } from "@/modules/recursos/simulacao";
import { useHistoricoSimulacoes } from "@/modules/recursos/simulacao/use-historico-simulacoes";
import type { HistoricoQueryStatus } from "@/modules/recursos/simulacao/use-historico-simulacoes";

import {
  HISTORICO_COLUMN_KEYS,
  HISTORICO_FILTRO_STATUS_KEY,
  HISTORICO_FILTRO_STATUS_LABEL,
  HISTORICO_STATUS_OPCOES,
} from "./historico-simulacao-dialog.constants";
import { toHistoricoLinha } from "./historico-simulacao-dialog.utils";
import type {
  HistoricoColumnKey,
  HistoricoDataColumn,
  HistoricoSimulacaoDialogProps,
  HistoricoSimulacaoDialogViewModel,
  HistoricoSortField,
} from "./types-historico-simulacao-dialog";

const FILTER_COLUMNS: FilterDefinition[] = [
  {
    key: HISTORICO_FILTRO_STATUS_KEY,
    label: HISTORICO_FILTRO_STATUS_LABEL,
    type: "select",
    options: HISTORICO_STATUS_OPCOES,
  },
];

// O core não ordena o histórico: nenhum campo passa, e o cabeçalho não oferece ordem.
const isHistoricoSortField = (_valor: unknown): _valor is HistoricoSortField => false;

/**
 * Histórico de simulações (HU 2221, RF07): listagem paginada no servidor, com filtro de status.
 * Abrir uma linha abre o detalhe da simulação por cima do histórico, que segue montado.
 */
export function useHistoricoSimulacaoDialog({
  onClose,
}: HistoricoSimulacaoDialogProps): HistoricoSimulacaoDialogViewModel {
  const toast = useToast();
  const [statusFiltro, setStatusFiltro] = useState<HistoricoQueryStatus>(null);
  const [detalheId, setDetalheId] = useState<string | null>(null);
  const colunas = useTableSettings<HistoricoDataColumn>({
    densityKey: HISTORICO_SIMULACAO_TABLE_DENSITY_KEY,
    allColumns: HISTORICO_COLUMN_KEYS,
    mainColumns: HISTORICO_COLUMN_KEYS,
  });
  // Erro e carregamento zeram o total da lista: a página só volta à 1ª com um total real menor.
  const { lastTotal, remember } = useLastKnownTotal();
  const table = useTableState<HistoricoSortField>({
    isSortField: isHistoricoSortField,
    total: lastTotal,
  });

  const lista = useHistoricoSimulacoes({
    page: table.page + 1,
    pageSize: table.rows,
    status: statusFiltro,
  });
  remember(lista.status, lista.total);

  const linhas = useMemo(() => lista.itens.map(toHistoricoLinha), [lista.itens]);

  const settings = useMemo(
    () => ({
      primeSize: colunas.primeSize,
      dialog: colunas.dialog,
      // A coluna da lupa não é configurável: sempre aparece.
      isColumnVisible: (coluna: HistoricoColumnKey) =>
        coluna === "acoes" || colunas.isColumnVisible(coluna),
    }),
    [colunas],
  );

  function aplicarFiltros(value: FilterValue) {
    const [status] = value[HISTORICO_FILTRO_STATUS_KEY] ?? [];
    setStatusFiltro((status as HistoricoQueryStatus | undefined) ?? null);
    table.resetPage();
  }

  function abrirDetalhe(id: string) {
    setDetalheId(id);
  }

  function fecharDetalhe() {
    setDetalheId(null);
  }

  function limparFiltros() {
    setStatusFiltro(null);
    table.resetPage();
  }

  return {
    titulo: HISTORICO_SIMULACAO_TITULO,
    fechar: onClose,
    detalheId,
    abrirDetalhe,
    fecharDetalhe,
    filterColumns: FILTER_COLUMNS,
    filterValue: statusFiltro ? { [HISTORICO_FILTRO_STATUS_KEY]: [statusFiltro] } : {},
    aplicarFiltros,
    linhas,
    total: lista.total,
    status: lista.status,
    errorMessage: lista.errorMessage,
    table,
    settings,
    hasFilters: statusFiltro !== null,
    limparFiltros,
    tentarNovamente: lista.reload,
    avisarConfiguracoesAplicadas: () => toast.success("Sucesso", TABLE_SETTINGS_APPLIED_MESSAGE),
  };
}
